"use client";

/**
 * MobileMenu component
 * Renders the mobile navigation menu, including drawer, navigation links, and login/goto pass logic.
 * Handles navigation datasource and conditional rendering for mobile experience.
 */

import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerTrigger,
} from "@/components/atoms/drawer";
import { MOBILE_BREAKPOINT } from "@/constants";
import { useIsMobile } from "@/hooks/useIsMobile/useIsMobile";
import {
  LOCALE_NAMES,
  LOCALE_SELECT_PLACEHOLDER,
  Locale,
  getPathname,
  routing,
  usePathname,
} from "@/i18n/routing";
import {
  LinkTarget,
  cn,
  generateLinkFromGeneralLink,
  generateTargetFromLink,
} from "@/lib/utils";
import { NavigationDatasourcesQuery } from "@/types/generated";
import {
  BOTTOM_NAVIGATION,
  BOTTOM_NAVIGATION_ITEM_NAME,
  GO_TO_PASS_ITEM_NAME,
  LOGIN_BUTTON,
  LOGIN_BUTTON_ITEM_NAME,
} from "@/utils";
import { Check, Globe } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/atoms/accordian";
import { useLocale } from "next-intl";
import Image from "next/image";
import { NavLink } from "../footer/BottomFooter";
import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/atoms/button";
import { Icon } from "@/components/atoms/icon";

interface MobileMenuProps {
  navigationDatasources?: Omit<NavigationDatasourcesQuery, "__typename">;
  isShowGotoPass?: boolean | null;
  isCustomTemplate?: boolean | null;
  enableAuth?: boolean;
}

interface NavMenu {
  itemName?: string;
  title?: string | null;
  url?: string | null;
  icon?: {
    cloudflare_url?: string | null;
    url?: string | null;
    alt_text?: string | null;
    caption?: string | null;
  };
}

export default function MobileMenu({
  navigationDatasources,
  isShowGotoPass,
  enableAuth,
}: Readonly<MobileMenuProps>) {
  const pathname = usePathname();
  const locale = useLocale();
  const isMobile = useIsMobile(MOBILE_BREAKPOINT);
  const [session, setSession] = useState('');

  useEffect(() => {
    const fetchSession = async () => {
      try {
        const sessionRes = await fetch('/api/getSession');
        if (!sessionRes.ok) {
          throw new Error('Failed to fetch session');
        }
        const sessionData = await sessionRes.json();
        setSession(sessionData?.session)
      } catch (error) {
        console.log('Error fetching session:', error);
      }
    };

    fetchSession();
  }, []);


  const onChangeLanguage = useCallback(
    (nextLocale: Locale) => {
      const url = getPathname({
        href: { pathname, query: {} },
        locale: nextLocale,
      });
      globalThis.location.assign(url); // slightly safer than href=
    },
    [pathname],
  );

  const documentId = isMobile
    ? "65804619fec40b3fa0541ee4"
    : "72ec2890fa3a6dade80b3fcf";

  const sideNavigation =
    navigationDatasources?.mystay?.shared_datasource?.navigation_datasources.find(
      (navigation_datasource) =>
        navigation_datasource?.documentId === documentId,
    );

  const selectedMenu = sideNavigation?.display_menu_items || [];
  const menuItems = sideNavigation?.menu_items;
  const sortedMenus = selectedMenu
    .map((selectedItem: { documentId: string }) => {
      const menuItem = menuItems?.find(
        (item) => item?.documentId === selectedItem.documentId,
      );
      if (!menuItem) return null;
      if (session) {
        return {
          itemName: menuItem.item_name,
          title: menuItem.title_after_loggin || menuItem.menu_title || null,
          url:
            generateLinkFromGeneralLink(
              menuItem.button_link_after_loggin || menuItem.menu_link,
              locale,
            ) || "#",
          icon: menuItem.icon ?? {},
        };
      }
      return {
        itemName: menuItem.item_name,
        title: menuItem.menu_title || null,
        url: generateLinkFromGeneralLink(menuItem.menu_link, locale) || "#",
        icon: menuItem.icon ?? {},
      };
    })
    .filter(
      (item: Partial<NavMenu> | null): item is Partial<NavMenu> =>
        !!item && item.title !== null,
    );

  const bottomNavigation =
    navigationDatasources?.mystay?.shared_datasource?.navigation_datasources.find(
      (item) =>
        item?.item_name === BOTTOM_NAVIGATION_ITEM_NAME ||
        item?.documentId === BOTTOM_NAVIGATION,
    );
  const actions: NavLink[] = bottomNavigation?.menu_items
    ? bottomNavigation.menu_items
      ?.map((item) => {
        if (session) {
          return {
            itemName: item?.item_name,
            documentId: item?.documentId,
            title: item?.title_after_loggin ?? item?.menu_title ?? "",
            show: item?.show,
            link:
              generateLinkFromGeneralLink(
                item?.button_link_after_loggin || item?.menu_link,
                locale,
              ) || "#",
            target: generateTargetFromLink(
              item?.button_link_after_loggin || item?.menu_link,
            ),
            defaultLink: generateLinkFromGeneralLink(
              item?.button_link_after_loggin,
              locale,
            ) || "#",
          };
        } else {
          return {
            itemName: item?.item_name,
            documentId: item?.documentId,
            title: item?.menu_title,
            show: item?.show,
            link: generateLinkFromGeneralLink(item?.menu_link, locale) || "#",
            target: generateTargetFromLink(item?.menu_link),
            defaultLink: generateLinkFromGeneralLink(
              item?.button_link_after_loggin,
              locale,
            ) || "#",
          };
        }
      })
      .filter((item) => !!item.title && !!item.documentId)
    : [];
  const actionLogin = actions?.find(
    (a) =>
      a.itemName === LOGIN_BUTTON_ITEM_NAME || a.documentId === LOGIN_BUTTON,
  );
  const handleDrawerOpenChange = (open: boolean) => {
    if (!open) {
      document.documentElement.style.scrollBehavior = "auto";
    }
    setTimeout(() => {
      document.documentElement.style.scrollBehavior = "smooth";
    }, 1000);
  };

  const renderActionLogin = (() => {
    if (!actionLogin?.show) return null;

    const isExternalLink = actionLogin?.link && actionLogin.link !== "#";

    if (isExternalLink && !session) {
      return (
        <a
          target={actionLogin?.target as LinkTarget}
          className="text-white"
          href={enableAuth ? `${actionLogin?.link}?inAppRedirect=${pathname}` : `${actionLogin?.defaultLink}`}

        >
          {actionLogin.title}
        </a>
      );
    }
  })();

  return (
    <Drawer onOpenChange={handleDrawerOpenChange}>
      <div className="flex flex-col items-center justify-center">
        <DrawerTrigger asChild>
          <Button
            className="h-6 p-0 hover:bg-white active:bg-white"
            hierarchy={"ghost"}
            aria-label="Open Menu"
          >
            <Icon icon={"menu"} />
          </Button>
        </DrawerTrigger>
        <div className="text-2xs leading-3 text-[var(--fill-base-main)] uppercase">
          Menu
        </div>
      </div>
      <DrawerContent className="fixed top-0 right-0 h-full w-80 bg-[var(--fill-base-main)]">
        <div className="flex flex-col gap-4 overflow-y-auto px-6 py-4">
          <div
            className={`flex items-center ${(!session && actionLogin?.show) ? "justify-between" : "justify-end"
              } py-2 pl-4`}
          >
            {renderActionLogin}
            <DrawerClose asChild>
              <Button
                hierarchy="outline"
                className="h-9 w-9 rounded-full border-none p-0"
              >
                <Icon icon={"close"} />
              </Button>
            </DrawerClose>
          </div>
          <nav className="flex flex-col items-start">
            {sortedMenus
              ?.filter(
                (item: Partial<NavMenu>) =>
                  item.itemName !== GO_TO_PASS_ITEM_NAME,
              )
              .map((menu: Partial<NavMenu>, index: number) => {
                const title = menu?.title ?? "";
                const url = menu?.url ?? "";
                const mobileMenuKey = `mobile-menu-${index}`;
                return (
                  <DrawerClose asChild key={mobileMenuKey}>
                    <Button
                      className={cn(
                        "w-full justify-start py-4 text-[var(--text-gray-base)] hover:bg-[var(--fill-base-white)] active:bg-[var(--fill-base-white)]",
                        "hover:text-[var(--text-base-primary)] active:text-[var(--text-base-primary)]",
                      )}
                      hierarchy={"ghost"}
                      onClick={() => {
                        globalThis.location.href = url?.startsWith("https")
                          ? url
                          : url || "";
                      }}
                    >
                      <span>{title}</span>
                    </Button>
                  </DrawerClose>
                );
              })}
            {!!isShowGotoPass &&
              sortedMenus
                ?.filter(
                  (item: Partial<NavMenu>) =>
                    item.itemName === GO_TO_PASS_ITEM_NAME,
                )
                .map((menu: Partial<NavMenu>, index: number) => {
                  const title = menu?.title ?? "";
                  const url = menu?.url ?? "";
                  const icon = menu?.icon ?? {};
                  const sortedMenuKey = `sorted-menu${index}`;
                  return (
                    <DrawerClose
                      asChild
                      key={sortedMenuKey}
                      className="mt-6 !bg-[var(--button-go-to-pass-bg)]"
                    >
                      <button
                        onClick={() => {
                          globalThis.location.href = url?.startsWith("https")
                            ? url
                            : url || "";
                        }}
                        className={cn(
                          "flex h-[50px] w-full cursor-pointer items-center justify-center gap-1 rounded-none px-1 py-4 text-white",
                        )}
                      >
                        <p className="text-sm text-white">{title}</p>
                        {(icon.cloudflare_url || icon.url) && (
                          <div className="relative h-5 w-auto min-w-5">
                            <Image
                              src={icon.cloudflare_url || icon.url || ""}
                              alt={icon.alt_text || ""}
                              aria-label={icon.caption || ""}
                              fill
                              className="h-full w-full"
                            />
                          </div>
                        )}
                      </button>
                    </DrawerClose>
                  );
                })}
          </nav>
          <div className="mt-6 flex flex-col gap-1.5">
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="language" className="border-none">
                <AccordionTrigger className="my-3 w-full min-w-[184.75px] justify-between rounded-lg px-4 py-2 font-[700] text-[var(--text-gray-base)] hover:bg-[var(--fill-base-white)] hover:text-[var(--text-base-primary)]">
                  <div className="flex items-center gap-2">
                    <Globe className="h-5 w-5" />
                    <span data-wovn-ignore>{LOCALE_SELECT_PLACEHOLDER}</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  {routing.locales.map((lang) => (
                    <div
                      key={lang}
                      data-wovn-ignore
                      aria-current={locale === lang ? "true" : undefined}
                      onClick={() => onChangeLanguage(lang)}
                      className={cn(
                        "my-2 flex w-full cursor-pointer items-center justify-between rounded-lg px-2 py-1 text-[var(--text-gray-lighter)] hover:bg-[var(--fill-base-white)] hover:text-[var(--text-base-primary)]",
                        locale === lang &&
                          "bg-[var(--fill-base-white)] text-[var(--text-base-primary)]",
                      )}
                    >
                      <span>{LOCALE_NAMES[lang]}</span>
                      {locale === lang && <Check className="h-4 w-4 shrink-0" />}
                    </div>
                  ))}
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
